import React from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Box, Typography, CircularProgress, Button, Paper } from '@mui/material';
import { Lock as LockIcon } from '@mui/icons-material';
import { AuthService, UserRole } from '../../services/AuthService';

interface RoleProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles: UserRole[];
  redirectTo?: string; // Optional redirect path instead of showing access denied
}

/**
 * Route guard component that checks user roles
 * 
 * Usage:
 * <RoleProtectedRoute allowedRoles={['super_admin', 'members_admin']}>
 *   <AdminMembersPage />
 * </RoleProtectedRoute>
 */
export const RoleProtectedRoute: React.FC<RoleProtectedRouteProps> = ({ 
  children, 
  allowedRoles,
  redirectTo 
}) => {
  const { currentUser, isAdmin, loading, userProfile } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const handleSignOut = async () => {
    try {
      await AuthService.signOutUser();
    } catch (err) {
      console.error('Sign-out failed:', err);
    }
    navigate('/login', { replace: true });
  };

  try {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress />
        </Box>
      );
    }

    if (currentUser && !userProfile) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '60vh',
            gap: 2,
            p: 3,
            textAlign: 'center',
          }}
        >
          <Typography>
            We could not open your account. Log out and sign in again.
          </Typography>
          <Button variant="contained" onClick={handleSignOut}>
            Log out
          </Button>
        </Box>
      );
    }

    // Not logged in - redirect to login
    if (!currentUser) {
      return <Navigate to="/login" replace />;
    }

    if (userProfile?.mustChangePassword && location.pathname !== '/profile') {
      return <Navigate to="/profile?forcePasswordChange=1" replace />;
    }

    // Not an admin at all - redirect to dashboard
    if (!isAdmin) {
      return <Navigate to="/dashboard" replace />;
    }

    // Get the user's role, with backwards compatibility
    const getUserRole = (): UserRole => {
      if (userProfile?.role) {
        return userProfile.role as UserRole;
      }
      // Backwards compatibility: if no role but isAdmin is true, treat as super_admin
      if (userProfile?.isAdmin) {
        return 'super_admin';
      }
      return 'member';
    };

    const userRole = getUserRole();

    // Check if user's role is in the allowed roles
    const hasAccess = allowedRoles.includes(userRole);

    if (!hasAccess) {
      // If redirectTo is specified, redirect there
      if (redirectTo) {
        return <Navigate to={redirectTo} replace />;
      }

      // Otherwise show access denied page
      return (
        <Box 
          sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            minHeight: '60vh',
            p: 3 
          }}
        >
          <Paper 
            elevation={3} 
            sx={{ 
              p: 4, 
              textAlign: 'center', 
              maxWidth: 400,
              bgcolor: 'background.paper'
            }}
          >
            <LockIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
            <Typography variant="h5" color="error" gutterBottom fontWeight={600}>
              Access Denied
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              You do not have permission to access this page. 
              This page requires one of the following roles: {allowedRoles.map(r => 
                r.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())
              ).join(', ')}.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button 
                variant="outlined" 
                onClick={() => window.history.back()}
              >
                Go Back
              </Button>
              <Button 
                variant="contained" 
                href="/admin"
              >
                Admin Dashboard
              </Button>
            </Box>
          </Paper>
        </Box>
      );
    }

    return <>{children}</>;
  } catch (error) {
    console.error('RoleProtectedRoute - error:', error);
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <Typography color="error">Error loading protected route</Typography>
      </Box>
    );
  }
};
